import React, { useState, useEffect } from "react";
import {
  Button, FormGroup, Label, Input, Row, Col, Form, Spinner
} from "reactstrap";
import { AlumnoService } from "service/AlumnoService";

const alumnoService = new AlumnoService();

const emptyAlumno = {
  documentoidentidad: "",
  nombres: "",
  apellidopaterno: "",
  apellidomaterno: "",
  urlimagen: "",
  estado: "1"
};

const TabInfoAlumno = ({ alumnoId, onAlumnoReady, notify }) => {
  const [alumno, setAlumno] = useState(emptyAlumno);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resettingPass, setResettingPass] = useState(false);
  const [loadingAlumno, setLoadingAlumno] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (alumnoId) {
      setIsEditing(true);
      loadAlumno(alumnoId);
    } else {
      setAlumno(emptyAlumno);
      setIsEditing(false);
    }
  }, [alumnoId]);

  const loadAlumno = async (id) => {
    setLoadingAlumno(true);
    try {
      const res = await alumnoService.getAlumnoId(id);
      if (res?.data) {
        setAlumno(res.data);
        if (onAlumnoReady) onAlumnoReady(res.data);
      }
    } catch (error) {
      notify("danger", "Error al cargar datos del alumno");
    } finally {
      setLoadingAlumno(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAlumno(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    if (name === "documentoidentidad" && value.length === 8 && !isEditing) {
      buscarPorDNI(value);
    }
  };

  const validate = () => {
    const e = {};
    if (!alumno.documentoidentidad || alumno.documentoidentidad.length !== 8)
      e.documentoidentidad = "El DNI debe tener exactamente 8 dígitos";
    if (!alumno.nombres?.trim())
      e.nombres = "Los nombres son obligatorios";
    if (!alumno.apellidopaterno?.trim())
      e.apellidopaterno = "El apellido paterno es obligatorio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buscarPorDNI = async (dni) => {
    try {
      const res = await alumnoService.getAlumnoDNI({ documentoidentidad: dni });
      if (res?.data?.length > 0) {
        const found = res.data[0];
        setAlumno(found);
        setIsEditing(true);
        if (onAlumnoReady) onAlumnoReady(found, found._id);
        notify("info", "Alumno encontrado. Accede a la pestaña Matrículas para gestionarlas.");
      }
    } catch (error) {
      console.error("Error buscando alumno:", error);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      let res;
      if (isEditing) {
        res = await alumnoService.updateAlumno(alumno);
        if (res?.confirmation === "success") {
          notify("success", "Datos actualizados correctamente");
        }
      } else {
        const nuevoAlumno = {
          ...alumno,
          usuario: {
            nombreusuario: alumno.documentoidentidad,
            contrasena: (alumno.nombres.charAt(0) + alumno.apellidopaterno.charAt(0) + alumno.documentoidentidad).toLowerCase(),
            rol: "Alumno"
          }
        };
        res = await alumnoService.addAlumno(nuevoAlumno);
        if (res?.data?.length > 0) {
          const created = res.data[0];
          setAlumno(created);
          setIsEditing(true);
          if (onAlumnoReady) onAlumnoReady(created, created._id);
          notify("success", "Alumno registrado exitosamente. Ya puedes agregar matrículas.");
        }
      }
    } catch (error) {
      notify("danger", "Error al guardar los datos del alumno");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm("¿Restablecer la contraseña del alumno a los valores por defecto?")) return;
    setResettingPass(true);
    try {
      const dto = {
        nombreusuario: alumno.documentoidentidad,
        contrasena: (alumno.nombres.charAt(0) + alumno.apellidopaterno.charAt(0) + alumno.documentoidentidad).toLowerCase(),
        rol: "Alumno"
      };
      const res = await alumnoService.resetPassword(alumno._id, dto);
      if (res?.confirmation === "success") {
        notify("success", "Contraseña restablecida correctamente");
      } else {
        notify("danger", res?.message || "Error al restablecer contraseña");
      }
    } catch (error) {
      notify("danger", "Error al restablecer contraseña");
    } finally {
      setResettingPass(false);
    }
  };

  if (loadingAlumno) {
    return (
      <div className="text-center py-5">
        <Spinner color="info" />
        <p className="mt-2 text-muted">Cargando datos del alumno...</p>
      </div>
    );
  }

  return (
    <Form>
      <Row>
        <Col md="3">
          <FormGroup>
            <Label>DNI *</Label>
            <Input
              name="documentoidentidad"
              value={alumno.documentoidentidad}
              onChange={handleChange}
              maxLength="8"
              disabled={isEditing}
              placeholder="08 dígitos"
              invalid={!!errors.documentoidentidad}
            />
            {errors.documentoidentidad
              ? <small className="text-danger">{errors.documentoidentidad}</small>
              : !isEditing && <small className="text-muted">Al ingresar 8 dígitos se buscará el alumno automáticamente</small>
            }
          </FormGroup>
        </Col>
        <Col md="9">
          <FormGroup>
            <Label>Nombres *</Label>
            <Input
              name="nombres"
              value={alumno.nombres}
              onChange={handleChange}
              placeholder="Nombres completos"
              invalid={!!errors.nombres}
            />
            {errors.nombres && <small className="text-danger">{errors.nombres}</small>}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label>Apellido Paterno *</Label>
            <Input
              name="apellidopaterno"
              value={alumno.apellidopaterno}
              onChange={handleChange}
              invalid={!!errors.apellidopaterno}
            />
            {errors.apellidopaterno && <small className="text-danger">{errors.apellidopaterno}</small>}
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>Apellido Materno</Label>
            <Input
              name="apellidomaterno"
              value={alumno.apellidomaterno || ""}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>URL Imagen</Label>
            <Input
              name="urlimagen"
              value={alumno.urlimagen || ""}
              onChange={handleChange}
              placeholder="https://..."
            />
          </FormGroup>
        </Col>
      </Row>

      <hr />

      <div className="d-flex align-items-center" style={{ gap: "8px" }}>
        <Button color="info" size="sm" onClick={handleSave} disabled={loading}>
          {loading ? <Spinner size="sm" /> : (isEditing ? "Actualizar Datos" : "Registrar Alumno")}
        </Button>
        {isEditing && (
          <Button color="warning" size="sm" onClick={handleResetPassword} disabled={resettingPass}>
            {resettingPass ? <Spinner size="sm" /> : "Restablecer Contraseña"}
          </Button>
        )}
      </div>

      {isEditing && alumno._id && (
        <small className="text-muted d-block mt-2">
          La contraseña por defecto es: <strong>{(alumno.nombres.charAt(0) + alumno.apellidopaterno.charAt(0) + alumno.documentoidentidad).toLowerCase()}</strong>
        </small>
      )}
    </Form>
  );
};

export default TabInfoAlumno;
